import { useCallback } from 'react';
import { deleteDocument } from 'src/services/WorkDocsService';
import { useWorkDocsAuth } from 'src/hooks/useWorkDocsAuth';
import { logger, handleError } from 'src/logger';

interface UseWorkDocsDeleteArgs {
  /**
   * Called after a document deletes successfully, with the folder it was removed from (used to
   * refresh the browser listing so the row disappears). May be async; the hook awaits it before
   * reporting success.
   */
  onDeleted?: (parentFolderId: string) => void | Promise<void>;
}

interface UseWorkDocsDeleteReturn {
  /**
   * Deletes one document, then runs the `onDeleted` refresh. Success tracks the DELETE alone:
   * returns `true` once the document is deleted server-side, even if the follow-up refresh throws
   * (the refresh failure is logged, not surfaced — retrying a completed delete would fail on the
   * now-missing document). Returns `false` only when the delete itself fails. Both errors are
   * logged; the caller owns any user-facing progress/error UI.
   */
  deleteFile: (parentFolderId: string, documentId: string) => Promise<boolean>;
}

/**
 * Deletes a single uploaded document from a WorkDocs folder. This is the side-effect executor
 * (mirrors {@link useWorkDocsUpload}): it performs the authenticated delete and the follow-up
 * folder refresh, and reports success as a boolean. Confirmation and progress/error display live
 * in the caller (see {@link useDeleteConfirmation}), keeping this hook single-purpose and the
 * calling component presentation-only.
 */
export const useWorkDocsDelete = ({ onDeleted }: UseWorkDocsDeleteArgs = {}): UseWorkDocsDeleteReturn => {
  const { accessToken } = useWorkDocsAuth();

  const deleteFile = useCallback(
    async (parentFolderId: string, documentId: string): Promise<boolean> => {
      if (!accessToken) {
        logger.warn('Baseball Cards: delete attempted without a WorkDocs token');
        return false;
      }

      // The DELETE is the authoritative, irreversible operation — success must track it alone.
      try {
        await deleteDocument(documentId, accessToken);
      } catch (error) {
        handleError('Baseball Cards: WorkDocs delete failed', error);
        return false;
      }

      // The refresh is cosmetic reconciliation (re-list the folder so the row disappears). If it
      // fails, the delete already succeeded, so we still report success — surfacing this as a
      // failure would prompt a retry that deletes a now-missing document and fails in a loop.
      try {
        await onDeleted?.(parentFolderId);
      } catch (error) {
        handleError('Baseball Cards: post-delete folder refresh failed', error);
      }
      return true;
    },
    [accessToken, onDeleted]
  );

  return { deleteFile };
};
